/**
 * OS chrome theme-color sync — Phase 7-theme (14 Jun 2026).
 *
 * Keeps the `<meta name="theme-color">` tag in index.html dynamically
 * aligned with the current guest's partner attribution. When a guest
 * is partner-attributed (e.g. Harlyn Sands), the iOS Safari address
 * bar / iOS standalone status bar / Android Chrome bar all take on
 * the partner's brand colour. Bare-town guests stay on TownInsider
 * default (`#0a4d3c`, moor-700).
 *
 * Premium partner-aware experience already extends to bubbles, avatar,
 * compass icon, and join-page heading via `partner.theme.primaryHex`.
 * This module pushes the same colour through to the operating system
 * chrome — closing the last point where partner branding fell back to
 * TownInsider house style.
 *
 * Architecture: side-effect logic (setThemeColor) is decoupled from
 * data flow (where the hex comes from). The hook takes the hex as
 * input rather than reading from `usePartner()` directly — keeps the
 * consumer free to source the value however it likes (auth'd cache,
 * pre-signup partner data from the redeem call, hard-coded test value)
 * and makes the side-effect testable in isolation without mocking
 * TanStack Query.
 *
 * Cleanup on unmount restores the meta tag to DEFAULT_THEME_COLOR.
 * Mounted at App.tsx level (alongside UpdateBanner), the hook
 * effectively never unmounts during a real session — cleanup only
 * runs on dependency change (hex transitioning from one value to
 * another) or whole-app teardown (page navigation away).
 *
 * Defensive: setThemeColor no-ops in SSR (no document) or when the
 * meta tag has been removed by another consumer. The base meta tag
 * is always shipped in index.html so the missing-tag case shouldn't
 * happen in practice, but premium robustness wins over assumption.
 */

import { useEffect } from 'react';

/**
 * Base TownInsider OS chrome colour — moor-700, matching the default
 * `<meta name="theme-color">` value shipped in index.html. Restored
 * on cleanup so unattributed guests (and post-signout sessions) see
 * the house chrome rather than a stuck partner colour.
 */
export const DEFAULT_THEME_COLOR = '#0a4d3c';

/**
 * Update the `<meta name="theme-color">` content attribute. Pure
 * side-effect helper, exported separately so callers can drive it
 * imperatively (e.g. from a one-shot effect during onboarding when
 * partner data lands before the React tree mounts the effect hook).
 *
 * No-op in environments without a document (jsdom test setup with
 * happy-dom disabled, SSR) or when the meta tag is missing entirely.
 */
export function setThemeColor(hex: string): void {
  if (typeof document === 'undefined') return;
  const meta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]',
  );
  if (!meta) return;
  meta.setAttribute('content', hex);
}

/**
 * Effect hook that keeps the OS chrome theme-color in sync with the
 * provided hex value, restoring to DEFAULT_THEME_COLOR on unmount or
 * when the hex transitions to null/undefined.
 *
 * Inputs:
 *   - hex `string` → set chrome to that colour
 *   - hex `null` or `undefined` → set chrome to DEFAULT_THEME_COLOR
 *
 * The hook does not validate the hex format — the caller is responsible
 * for supplying a colour value the browser will accept as a meta
 * theme-color (CSS colour string, typically `#RRGGBB`). Invalid values
 * are silently ignored by the browser, which is the right failure mode
 * for a non-critical chrome enhancement.
 *
 * Re-runs when `hex` changes — partner data loading mid-session
 * (TanStack Query resolves /me) transitions undefined → partner hex,
 * effect fires, chrome updates. Same path on sign-out (cache cleared,
 * usePartner returns undefined) → chrome restores to default.
 */
export function useThemeColorEffect(hex: string | null | undefined): void {
  useEffect(() => {
    const target = hex ?? DEFAULT_THEME_COLOR;
    setThemeColor(target);
    return () => {
      // Always restore to default on cleanup. The hook is mounted
      // globally and persists for the app lifetime, so cleanup only
      // fires on (a) dep change — followed immediately by re-running
      // the effect with the new value, so this restore is overwritten
      // in the same tick — or (b) whole-app teardown, where the
      // restore primes the meta tag for a clean state if the SPA
      // re-mounts without a full page reload.
      setThemeColor(DEFAULT_THEME_COLOR);
    };
  }, [hex]);
}
